import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { motion } from 'framer-motion';
import { MapPin, Coffee, Utensils, Hotel, Wrench, Plus, Phone, Globe, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

const placeTypeIcons = {
  cafe: { icon: Coffee, color: '#F97316' },
  restaurant: { icon: Utensils, color: '#10B981' },
  hotel: { icon: Hotel, color: '#3B82F6' },
  garage: { icon: Wrench, color: '#8B5CF6' },
};

const MapPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPlace, setNewPlace] = useState({
    name: '',
    place_type: 'cafe',
    description: '',
    address: '',
    city: '',
    country: 'FR',
    latitude: 48.8566,
    longitude: 2.3522,
    phone: '',
    website: '',
    image_url: '',
  });

  const placeTypes = [
    { value: 'all', label: t('map.filters.all') },
    { value: 'cafe', label: t('map.filters.cafe') },
    { value: 'restaurant', label: t('map.filters.restaurant') },
    { value: 'hotel', label: t('map.filters.hotel') },
    { value: 'garage', label: t('map.filters.garage') },
  ];

  useEffect(() => {
    fetchPlaces();
  }, [typeFilter]);

  const fetchPlaces = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('place_type', typeFilter);
      
      const response = await axios.get(`${API_URL}/places?${params.toString()}`);
      setPlaces(response.data);
    } catch (error) {
      console.error('Error fetching places:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlace = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/places`, newPlace, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('common.success'));
      setDialogOpen(false);
      fetchPlaces();
      setNewPlace({
        name: '',
        place_type: 'cafe',
        description: '',
        address: '',
        city: '',
        country: 'FR',
        latitude: 48.8566,
        longitude: 2.3522,
        phone: '',
        website: '',
        image_url: '',
      });
    } catch (error) {
      console.error('Error adding place:', error);
      toast.error(t('common.error'));
    }
  };

  const PlaceIcon = ({ type }) => {
    const { icon: Icon, color } = placeTypeIcons[type] || placeTypeIcons.cafe;
    return <Icon className="w-5 h-5" style={{ color }} />;
  };

  return (
    <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem-2rem)] flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-8 py-4 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between bg-background border-b border-border">
        <h1 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight">
          {t('map.title')}
        </h1>
        <div className="flex gap-3 w-full md:w-auto">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-[180px]" data-testid="map-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {placeTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="brand-glow gap-2" data-testid="add-place-btn">
                <Plus className="w-4 h-4" />
                <span className="hidden md:inline">{t('map.addPlace')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl uppercase">
                  {t('map.addPlace')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddPlace} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>{i18n.language === 'fr' ? 'Nom' : 'Name'} *</Label>
                  <Input
                    value={newPlace.name}
                    onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value })}
                    required
                    data-testid="place-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select 
                    value={newPlace.place_type} 
                    onValueChange={(v) => setNewPlace({ ...newPlace, place_type: v })}
                  >
                    <SelectTrigger data-testid="place-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {placeTypes.filter(t => t.value !== 'all').map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{i18n.language === 'fr' ? 'Adresse' : 'Address'} *</Label>
                  <Input
                    value={newPlace.address}
                    onChange={(e) => setNewPlace({ ...newPlace, address: e.target.value })}
                    required
                    data-testid="place-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{i18n.language === 'fr' ? 'Ville' : 'City'} *</Label>
                    <Input
                      value={newPlace.city}
                      onChange={(e) => setNewPlace({ ...newPlace, city: e.target.value })}
                      required
                      data-testid="place-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('profile.country')} *</Label>
                    <Select 
                      value={newPlace.country} 
                      onValueChange={(v) => setNewPlace({ ...newPlace, country: v })}
                    >
                      <SelectTrigger data-testid="place-country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FR">{t('countries.FR')}</SelectItem>
                        <SelectItem value="DE">{t('countries.DE')}</SelectItem>
                        <SelectItem value="ES">{t('countries.ES')}</SelectItem>
                        <SelectItem value="IT">{t('countries.IT')}</SelectItem>
                        <SelectItem value="BE">{t('countries.BE')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Latitude *</Label>
                    <Input
                      type="number"
                      step="any"
                      value={newPlace.latitude}
                      onChange={(e) => setNewPlace({ ...newPlace, latitude: parseFloat(e.target.value) })}
                      required
                      data-testid="place-lat-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Longitude *</Label>
                    <Input
                      type="number"
                      step="any"
                      value={newPlace.longitude}
                      onChange={(e) => setNewPlace({ ...newPlace, longitude: parseFloat(e.target.value) })}
                      required
                      data-testid="place-lng-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newPlace.description}
                    onChange={(e) => setNewPlace({ ...newPlace, description: e.target.value })}
                    rows={2}
                    data-testid="place-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{i18n.language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                    <Input
                      value={newPlace.phone}
                      onChange={(e) => setNewPlace({ ...newPlace, phone: e.target.value })}
                      data-testid="place-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{i18n.language === 'fr' ? 'Site web' : 'Website'}</Label>
                    <Input
                      type="url"
                      value={newPlace.website}
                      onChange={(e) => setNewPlace({ ...newPlace, website: e.target.value })}
                      data-testid="place-website-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-place-btn">
                  {t('common.save')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Map & List */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer
            center={[48.8566, 2.3522]}
            zoom={6}
            className="h-64 md:h-full w-full"
            style={{ background: '#09090B' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            />
            {places.map((place) => (
              <Marker
                key={place.id}
                position={[place.latitude, place.longitude]}
                icon={createCustomIcon(placeTypeIcons[place.place_type]?.color || '#F97316')}
                eventHandlers={{
                  click: () => setSelectedPlace(place),
                }}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <h3 className="font-bold text-foreground">{place.name}</h3>
                    <p className="text-sm text-muted-foreground">{place.address}, {place.city}</p>
                    {place.phone && (
                      <a href={`tel:${place.phone}`} className="text-sm text-primary flex items-center gap-1 mt-1">
                        <Phone className="w-3 h-3" /> {place.phone}
                      </a>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Places List */}
        <div className="w-full md:w-80 lg:w-96 border-l border-border bg-card overflow-y-auto max-h-[40vh] md:max-h-full">
          <div className="p-4">
            <h2 className="font-heading font-bold text-lg uppercase mb-4">
              {places.length} {i18n.language === 'fr' ? 'lieux' : 'places'}
            </h2>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4">
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : places.length > 0 ? (
              <div className="space-y-3">
                {places.map((place) => (
                  <motion.div
                    key={place.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <Card 
                      className={`cursor-pointer hover:border-primary/50 transition-colors ${
                        selectedPlace?.id === place.id ? 'border-primary' : ''
                      }`}
                      onClick={() => setSelectedPlace(place)}
                      data-testid={`place-card-${place.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-10 h-10 rounded-md flex items-center justify-center"
                            style={{ backgroundColor: `${placeTypeIcons[place.place_type]?.color}20` }}
                          >
                            <PlaceIcon type={place.place_type} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm line-clamp-1">{place.name}</h3>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {place.city}, {t(`countries.${place.country}`)}
                            </p>
                            {place.rating > 0 && (
                              <div className="flex items-center gap-1 mt-1">
                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                <span className="text-xs">{place.rating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-sm">
                  {i18n.language === 'fr' ? 'Aucun lieu trouvé' : 'No places found'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
